// PR-I-83: BookingCard con 3 variantes visuales (experience / introduction / expertise).
// Discrimina por `item.type` y por `bucket` (active | pending | past) para renderizar
// el hero izquierdo, el header derecho y la meta-row apropiados. La info-box central
// (topic + message) y las acciones inferiores son comunes a las tres variantes.
//
// Notas de arquitectura:
// - Componente presentacional puro: sin fetch, sin storage, sin dangerouslySetInnerHTML.
// - Estilos aislados con prefijo `nb-booking-card*` (definidos en _nb-my-bookings.scss).
//   Los modifiers `--experience | --introduction | --expertise` extienden el bloque base
//   sin tocar la familia `.introductions-card*` (usada en /my-introductions en prod).
// - `getPastLabel` es la única fuente de verdad para el header derecho cuando bucket=past.
//   El chip "X days left to accept" en pending lee `item.acceptDeadline` (ISO) que el
//   back emite solo mientras la intro sigue pending.
// - Layout Figma (mb01/mb03/mb06):
//     Experience  -> hero panorámico con overlay de título; header izq = avatares
//                    (You + counterparty), header der = fecha suelta o chip Cancelled.
//                    Meta row con "N Guests".
//     Introduction/Expertise -> hero vertical con foto del expert + caption con nombre;
//                    header izq = "Type / General Introduction" o "Rate / $X/hour",
//                    header der = "Schedule On / <fecha>" o past label / chip.
//
// Seguridad (SSDLC):
// - No se registran logs con PII (title/message/counterparty/topic no van a console).
// - Todas las imágenes preservan `onError={handleImageErrorGray}` para evitar broken
//   image o intentos de fetch a rutas 404 rastreables.
// - Accesibilidad: role="button" + tabIndex + aria-label descriptivo por variante.
// - Sin HTML dinámico -> inmune a XSS reflejado por payload en `title`/`message`.

import { FC, KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { handleImageErrorGray, handleImageErrorProfile } from '../../../utils/UtilsFiles'
import { formatDateShortIntl2 } from '../../../helpers/FormatDate'
import { BookingBucket, BookingItem, BookingType } from '../models'
import {
  audienceLabel,
  formatDuration,
  formatMoney,
  formatPriceWithUnit,
  getPastLabel,
} from '../utils'

interface BookingCardProps {
  item: BookingItem
  /** Bucket activo en el shell; controla las variantes Active/Pending/Past. Default 'active'. */
  bucket?: BookingBucket
  /**
   * Avatar del member logueado (para la card "You" en experience bookings).
   * El back de bookings/summary no expone al viewer -- la page lo pasa desde Redux.
   */
  viewerAvatar?: string | null
  onOpenDetail?: (item: BookingItem) => void
  onSendMessage?: (item: BookingItem) => void
  /**
   * PR-I-84 (Armando 2026-09-09): navegar al detalle real de la experience.
   * Solo se usa desde el link VIEW DETAILS del box experience; el modal de
   * Full Policy / Cancel sigue en `onOpenDetail` (no-op este PR).
   */
  onViewExperience?: (item: BookingItem) => void
}

const TYPE_LABEL: Record<BookingType, string> = {
  experience: 'Experience',
  introduction: 'Introduction',
  expertise: 'Expertise',
}

// ---------------------------------------------------------------------------
// Helpers puros
// ---------------------------------------------------------------------------

/**
 * Handler a11y compartido para elementos con role="button" que no son <button>.
 * Activa el callback cuando el usuario presiona Enter o Space, previniendo el
 * scroll por defecto del Space. Nunca registra al usuario en logs.
 */
const handleKeyActivate = (handler: () => void) => (event: KeyboardEvent<HTMLElement>) => {
  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault()
    handler()
  }
}

/**
 * Header derecho para bucket=active/pending cuando aplica formato "Schedule On" / "Requested On".
 * Introduction y Expertise usan esta pareja label+fecha; Experience usa la fecha suelta.
 */
const getActiveDateLabel = (item: BookingItem): { label: string; value: string } | null => {
  if (item.scheduledAt) {
    return { label: 'Schedule On', value: formatDateShortIntl2(item.scheduledAt) }
  }
  if (item.createdAt) {
    return { label: 'Requested On', value: formatDateShortIntl2(item.createdAt) }
  }
  return null
}

/**
 * Header izquierdo textual para variantes introduction / expertise.
 * - expertise + precio -> "Rate / USD 30/hour" (regla PR-I-84: sin `$`, entero, coma).
 * - introduction / expertise sin precio -> "Type / <subtitle>" o "Type / <TYPE_LABEL>"
 */
const getTextualLeftHeader = (
  item: BookingItem,
): { label: string; value: string } => {
  if (item.type === 'expertise' && item.price) {
    const formatted =
      formatPriceWithUnit(item.price.amount, item.price.currency, item.price.unit) ||
      formatMoney(item.price.amount, item.price.currency)
    if (formatted) {
      return { label: 'Rate', value: formatted }
    }
  }
  const value =
    item.subtitle && item.subtitle.trim().length > 0 ? item.subtitle : TYPE_LABEL[item.type]
  return { label: 'Type', value }
}

/**
 * Días restantes para aceptar una intro (variante Pending).
 * Lee `item.acceptDeadline` (ISO) que el back emite solo cuando la intro
 * sigue pending. Ausente o vencido => retorna null y el chip no se muestra.
 */
const getPendingAcceptDaysLeft = (item: BookingItem): number | null => {
  if (!item.acceptDeadline) return null
  const deadline = new Date(item.acceptDeadline).getTime()
  if (Number.isNaN(deadline)) return null
  const diffMs = deadline - Date.now()
  if (diffMs <= 0) return null
  return Math.max(1, Math.ceil(diffMs / (24 * 60 * 60 * 1000)))
}

// ---------------------------------------------------------------------------
// Subcomponentes internos por variante (hero + header)
// ---------------------------------------------------------------------------

interface HeroProps {
  item: BookingItem
  onOpenDetail: () => void
}

/** Hero para EXPERIENCE: imagen panorámica full-height con overlay + título grande. */
const ExperienceHero: FC<HeroProps> = ({ item, onOpenDetail }) => (
  <div
    className='nb-booking-card-img-wrapper nb-booking-card-hero nb-booking-card-hero--experience cursor-pointer'
    onClick={onOpenDetail}
    onKeyDown={handleKeyActivate(onOpenDetail)}
    role='button'
    tabIndex={0}
    aria-label={`Open experience ${item.title}`}
  >
    {item.image ? (
      <img
        className='nb-booking-card-img'
        src={item.image}
        onError={handleImageErrorGray}
        alt={item.title}
      />
    ) : (
      <div className='nb-booking-card-img nb-booking-card-img-fallback' aria-hidden />
    )}
    <div className='nb-booking-card-img-overlay'>
      <div className='nb-booking-card-img-title'>{item.title}</div>
    </div>
  </div>
)

/**
 * Hero para INTRODUCTION: retrato vertical del expert con caption inferior tipo serif
 * (referencia Figma mb01/mb03/mb06). Si no hay imagen, cae a un placeholder gris
 * manteniendo proporciones.
 */
const IntroductionHero: FC<HeroProps> = ({ item, onOpenDetail }) => {
  const expertName = item.counterparty?.name ?? item.title
  const image = item.image || item.counterparty?.avatar || null
  return (
    <div
      className='nb-booking-card-img-wrapper nb-booking-card-hero nb-booking-card-hero--introduction cursor-pointer'
      onClick={onOpenDetail}
      onKeyDown={handleKeyActivate(onOpenDetail)}
      role='button'
      tabIndex={0}
      aria-label={`Open introduction with ${expertName}`}
    >
      {image ? (
        <img
          className='nb-booking-card-img'
          src={image}
          onError={handleImageErrorGray}
          alt={expertName}
        />
      ) : (
        <div className='nb-booking-card-img nb-booking-card-img-fallback' aria-hidden />
      )}
      <div className='nb-booking-card-img-overlay nb-booking-card-img-overlay--portrait'>
        <div className='nb-booking-card-portrait-name'>{expertName}</div>
      </div>
    </div>
  )
}

/**
 * Hero para EXPERTISE: mismo patrón visual que INTRODUCTION (foto vertical + caption).
 * Diferencia semántica: el header derecho y el `aria-label` describen "expertise request".
 */
const ExpertiseHero: FC<HeroProps> = ({ item, onOpenDetail }) => {
  const expertName = item.counterparty?.name ?? item.title
  const image = item.image || item.counterparty?.avatar || null
  return (
    <div
      className='nb-booking-card-img-wrapper nb-booking-card-hero nb-booking-card-hero--expertise cursor-pointer'
      onClick={onOpenDetail}
      onKeyDown={handleKeyActivate(onOpenDetail)}
      role='button'
      tabIndex={0}
      aria-label={`Open expertise request with ${expertName}`}
    >
      {image ? (
        <img
          className='nb-booking-card-img'
          src={image}
          onError={handleImageErrorGray}
          alt={expertName}
        />
      ) : (
        <div className='nb-booking-card-img nb-booking-card-img-fallback' aria-hidden />
      )}
      <div className='nb-booking-card-img-overlay nb-booking-card-img-overlay--portrait'>
        <div className='nb-booking-card-portrait-name'>{expertName}</div>
      </div>
    </div>
  )
}

interface HeaderRightProps {
  item: BookingItem
  bucket: BookingBucket
}

/**
 * Renderiza el header derecho según bucket + type:
 * - past   -> getPastLabel (Cancelled On / Finished On / o chip Cancelled/Finished).
 * - pending + introduction/expertise -> "Schedule On / <fecha>" si hay scheduledAt;
 *   sino chip "X days left to accept" (introduction) o "Awaiting response" (expertise).
 * - pending + experience -> fecha suelta (createdAt / scheduledAt).
 * - active + experience -> fecha suelta (sin label, según Figma mb01).
 * - active + introduction/expertise -> "Schedule On / <fecha>".
 *
 * Justificación Figma (mb03): en Pending la card de Introduction muestra
 * "Schedule On / 12 June 2025" cuando ya hay fecha propuesta; el chip "days left"
 * queda como fallback cuando el request aún no tiene fecha agendada.
 */
const HeaderRight: FC<HeaderRightProps> = ({ item, bucket }) => {
  if (bucket === 'past') {
    const past = getPastLabel(item)
    const status = (item.status || '').toLowerCase()
    const isCancelled = status === 'cancelled' || status === 'canceled'
    // Chip visual sólo cuando no hay fecha (label === null y value es palabra clave).
    if (past.label === null && past.value && (isCancelled || status === 'completed' || status === 'finished')) {
      return (
        <div
          className={`nb-booking-card-status-chip ${
            isCancelled
              ? 'nb-booking-card-status-chip--cancelled'
              : 'nb-booking-card-status-chip--finished'
          }`}
        >
          {past.value}
        </div>
      )
    }
    if (past.label) {
      return (
        <>
          <div className='nb-booking-card-header-label'>{past.label}</div>
          <div className='nb-booking-card-header-date'>{past.value}</div>
        </>
      )
    }
    if (!past.value) return null
    return <div className='nb-booking-card-header-date'>{past.value}</div>
  }

  if (bucket === 'pending') {
    // Introduction/Expertise: si ya hay fecha propuesta, priorizar "Schedule On"
    // (Figma mb03). Los chips son fallback cuando aún no hay fecha.
    if (item.type === 'introduction' || item.type === 'expertise') {
      if (item.scheduledAt) {
        return (
          <>
            <div className='nb-booking-card-header-label'>Schedule On</div>
            <div className='nb-booking-card-header-date'>{formatDateShortIntl2(item.scheduledAt)}</div>
          </>
        )
      }
      if (item.type === 'introduction') {
        const daysLeft = getPendingAcceptDaysLeft(item)
        if (daysLeft !== null) {
          return (
            <div className='nb-booking-card-status-chip nb-booking-card-status-chip--pending'>
              {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left to accept
            </div>
          )
        }
      }
      if (item.type === 'expertise') {
        return (
          <div className='nb-booking-card-status-chip nb-booking-card-status-chip--pending'>
            Awaiting response
          </div>
        )
      }
      // Fallback silencioso: sin scheduledAt, sin deadline, sin chip.
      if (item.createdAt) {
        return (
          <>
            <div className='nb-booking-card-header-label'>Requested On</div>
            <div className='nb-booking-card-header-date'>{formatDateShortIntl2(item.createdAt)}</div>
          </>
        )
      }
      return null
    }
    // Pending + experience: fecha suelta (mismo patrón que Active experience).
    if (item.scheduledAt) {
      return <div className='nb-booking-card-header-date'>{formatDateShortIntl2(item.scheduledAt)}</div>
    }
    if (item.createdAt) {
      return <div className='nb-booking-card-header-date'>{formatDateShortIntl2(item.createdAt)}</div>
    }
    return null
  }

  // bucket === 'active'
  // Experience: sólo fecha suelta (sin label).
  if (item.type === 'experience') {
    if (item.scheduledAt) {
      return <div className='nb-booking-card-header-date'>{formatDateShortIntl2(item.scheduledAt)}</div>
    }
    if (item.createdAt) {
      return <div className='nb-booking-card-header-date'>{formatDateShortIntl2(item.createdAt)}</div>
    }
    return null
  }

  // Introduction / expertise: "Schedule On <fecha>".
  const dateLabel = getActiveDateLabel(item)
  if (!dateLabel) return null
  return (
    <>
      <div className='nb-booking-card-header-label'>{dateLabel.label}</div>
      <div className='nb-booking-card-header-date'>{dateLabel.value}</div>
    </>
  )
}

/**
 * Header izquierdo:
 * - Experience -> fila de avatares "You + <counterparty>". Sin label.
 * - Introduction / Expertise -> "Type / <subtitle>" o "Rate / $X/hour".
 */
interface HeaderLeftProps {
  item: BookingItem
  viewerAvatar?: string | null
}

const HeaderLeft: FC<HeaderLeftProps> = ({ item, viewerAvatar }) => {
  if (item.type === 'experience') {
    const counterpartyName = item.counterparty?.name ?? ''
    const counterpartyAvatar = item.counterparty?.avatar ?? null
    return (
      <div className='nb-booking-card-parties'>
        <div className='nb-booking-card-party'>
          {viewerAvatar ? (
            <img
              className='nb-booking-card-party-avatar'
              src={viewerAvatar}
              onError={handleImageErrorProfile}
              alt='You'
            />
          ) : (
            <div
              className='nb-booking-card-party-avatar nb-booking-card-party-avatar--fallback'
              aria-hidden
            />
          )}
          <span className='nb-booking-card-party-name'>You</span>
        </div>
        {counterpartyName && (
          <div className='nb-booking-card-party'>
            {counterpartyAvatar ? (
              <img
                className='nb-booking-card-party-avatar'
                src={counterpartyAvatar}
                onError={handleImageErrorProfile}
                alt={counterpartyName}
              />
            ) : (
              <div
                className='nb-booking-card-party-avatar nb-booking-card-party-avatar--fallback'
                aria-hidden
              />
            )}
            <span className='nb-booking-card-party-name nb-booking-card-party-name--counterparty'>
              {counterpartyName}
            </span>
          </div>
        )}
      </div>
    )
  }

  const { label, value } = getTextualLeftHeader(item)
  return (
    <>
      <div className='nb-booking-card-header-label'>{label}</div>
      <div className='nb-booking-card-header-value'>{value}</div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const BookingCard: FC<BookingCardProps> = ({
  item,
  bucket = 'active',
  viewerAvatar,
  onOpenDetail,
  onSendMessage,
  onViewExperience,
}) => {
  const shouldOfferReadMore = Boolean(item.message && item.message.length > 120)

  const handleOpenDetail = () => {
    onOpenDetail?.(item)
  }

  const handleSendMessage = () => {
    onSendMessage?.(item)
  }

  const handleViewExperience = () => {
    onViewExperience?.(item)
  }

  // Modifier CSS por variante -- permite ajustes finos sin duplicar el bloque base.
  const variantModifier = `nb-booking-card--${item.type}`

  // aria-label del wrapper: describe variante + bucket sin exponer datos sensibles.
  const wrapperAriaLabel = `${TYPE_LABEL[item.type]} booking (${bucket})`

  // ---- Datos derivados para experience meta-grid --------------------------
  const isExperience = item.type === 'experience'
  const extra = isExperience ? item.extra ?? null : null
  const experienceLocation = extra?.location || null
  const experienceDuration = formatDuration(extra?.duration, extra?.durationType)
  const experienceMaxGuests = extra?.maxGuests != null ? Number(extra.maxGuests) : null
  const experiencePricePerGuest = formatPriceWithUnit(
    extra?.pricePerGuest,
    item.price?.currency,
    'guest',
  )
  const experienceAudience = audienceLabel(extra?.audienceType)
  const experienceDateText = item.scheduledAt
    ? formatDateShortIntl2(item.scheduledAt)
    : item.createdAt
    ? formatDateShortIntl2(item.createdAt)
    : null
  const experienceCategories = Array.isArray(extra?.categories) ? extra?.categories ?? [] : []
  const experienceDescription = extra?.description || item.message || null

  // ---- Bloque de policy: aplica a los 3 tipos, excepto past ---------------
  // Regla PR-I-84 (Armando 2026-09-09): experience, introduction y expertise
  // mantienen "Non-Refundable / Full Policy / Cancel" en active/pending.
  const showPolicyBlock = bucket !== 'past'

  // ---- Reglas SEND MESSAGE (PR-I-84 + PR-I-85 fix 2026-09-10) -------------
  // - experience pending: OCULTAR (back exige Booking.STATUS_CONFIRMED, si no
  //   ->  chatService devuelve 403 y quema UX). Se muestra en active/past.
  // - expertise: NO usa el endpoint /chat/conversations/get-or-create/ (que no
  //   tiene branch para chat_type='expertise'); la solicitud ya trae abierto el
  //   hilo con reverse OneToOne, asi que el back emite `conversationUuid` en el
  //   item y aqui pintamos un <Link> directo a /messages?uuid=... — mismo patron
  //   que la card del lado experto en /my-expertise. Sin uuid dejamos el
  //   disabled con tooltip para no romper la UX si algun request quedo huerfano.
  // - introduction: normal en los 3 buckets (sigue via onSendMessage).
  const hideSendMessage = item.type === 'experience' && bucket === 'pending'
  const expertiseConversationUuid =
    item.type === 'expertise' ? item.conversationUuid ?? null : null
  const isSendMessageDisabled =
    item.type === 'expertise' && !expertiseConversationUuid
  const sendMessageTooltip = isSendMessageDisabled ? 'Coming soon' : undefined
  // Label del CTA de chat unificado para los 3 tipos: "MESSAGE YOUR HOST" en pending,
  // "SEND MESSAGE" en active/past. Asi la copia refleja el estado de la solicitud (aun
  // no confirmada -> pide al host que responda) sin bifurcar por variante.
  const sendMessageLabel =
    bucket === 'pending' ? 'MESSAGE YOUR HOST' : 'SEND MESSAGE'

  return (
    <div
      className={`nb-booking-card ${variantModifier}`}
      data-testid={`booking-card-${item.id}`}
      data-bucket={bucket}
      data-type={item.type}
      aria-label={wrapperAriaLabel}
    >
      {/* LEFT HERO */}
      {item.type === 'experience' && (
        <ExperienceHero item={item} onOpenDetail={handleViewExperience} />
      )}
      {item.type === 'introduction' && (
        <IntroductionHero item={item} onOpenDetail={handleOpenDetail} />
      )}
      {item.type === 'expertise' && (
        <ExpertiseHero item={item} onOpenDetail={handleOpenDetail} />
      )}

      {/* RIGHT CONTENT */}
      <div className='nb-booking-card-details'>
        {/* HEADER */}
        <div className='nb-booking-card-header'>
          <div className='nb-booking-card-header-left'>
            <HeaderLeft item={item} viewerAvatar={viewerAvatar} />
          </div>

          <div className='nb-booking-card-header-right'>
            <HeaderRight item={item} bucket={bucket} />
          </div>
        </div>

        {/* EXPERIENCE BOX — Figma mb01 v3 (PR-I-84):
             Un solo contenedor con borde gris: categorias (chips) + descripcion
             + meta-grid 3 cols x 2 filas (Location · Date · Max Guests /
             Price · Duration · vacio). Debajo del box (fuera): audience label
             en Cormorant rojo + VIEW DETAILS link subrayado. */}
        {isExperience ? (
          <>
            <div className='nb-booking-card-experience-box'>
              {experienceCategories.length > 0 && (
                <div className='nb-booking-card-chips'>
                  {experienceCategories.map((cat, idx) => (
                    <span
                      key={`${cat}-${idx}`}
                      className='nb-booking-card-chip'
                    >
                      {cat}
                    </span>
                  ))}
                </div>
              )}
              {experienceDescription && (
                <div className='nb-booking-card-experience-description'>
                  {experienceDescription}
                </div>
              )}
              <div className='nb-booking-card-meta-grid'>
                {experienceLocation && (
                  <div className='nb-booking-card-meta-cell'>
                    <svg
                      className='nb-booking-card-meta-icon'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                      focusable='false'
                    >
                      <path
                        d='M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8zm0 11a3 3 0 110-6 3 3 0 010 6z'
                        stroke='#7B7B7B'
                        strokeWidth='1.4'
                        fill='none'
                      />
                    </svg>
                    <span>{experienceLocation}</span>
                  </div>
                )}
                {experienceDateText && (
                  <div className='nb-booking-card-meta-cell'>
                    <svg
                      className='nb-booking-card-meta-icon'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                      focusable='false'
                    >
                      <rect x='3.5' y='5' width='17' height='15' rx='2' stroke='#7B7B7B' strokeWidth='1.4' fill='none' />
                      <path d='M3.5 9h17' stroke='#7B7B7B' strokeWidth='1.4' />
                      <path d='M8 3v4M16 3v4' stroke='#7B7B7B' strokeWidth='1.4' strokeLinecap='round' />
                    </svg>
                    <span>{experienceDateText}</span>
                  </div>
                )}
                {experienceMaxGuests != null && (
                  <div className='nb-booking-card-meta-cell'>
                    <svg
                      className='nb-booking-card-meta-icon'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                      focusable='false'
                    >
                      <path
                        d='M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-3.33 0-8 1.67-8 5v1h16v-1c0-3.33-4.67-5-8-5z'
                        fill='#7B7B7B'
                      />
                    </svg>
                    <span>Max {experienceMaxGuests} Guests</span>
                  </div>
                )}
                {experiencePricePerGuest && (
                  <div className='nb-booking-card-meta-cell'>
                    <svg
                      className='nb-booking-card-meta-icon'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                      focusable='false'
                    >
                      <circle cx='12' cy='12' r='9' stroke='#7B7B7B' strokeWidth='1.4' fill='none' />
                      <path
                        d='M14.5 9.2c-.6-.6-1.5-1-2.5-1-1.7 0-3 1-3 2.3s1.3 2 3 2 3 .7 3 2-1.3 2.3-3 2.3c-1 0-1.9-.4-2.5-1M12 6.5v11'
                        stroke='#7B7B7B'
                        strokeWidth='1.4'
                        strokeLinecap='round'
                        fill='none'
                      />
                    </svg>
                    <span>{experiencePricePerGuest}</span>
                  </div>
                )}
                {experienceDuration && (
                  <div className='nb-booking-card-meta-cell'>
                    <svg
                      className='nb-booking-card-meta-icon'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      aria-hidden='true'
                      focusable='false'
                    >
                      <circle cx='12' cy='12' r='9' stroke='#7B7B7B' strokeWidth='1.4' fill='none' />
                      <path d='M12 7v5l3 2' stroke='#7B7B7B' strokeWidth='1.4' strokeLinecap='round' />
                    </svg>
                    <span>{experienceDuration}</span>
                  </div>
                )}
              </div>
              {/* Audience + VIEW DETAILS DENTRO del box (Figma mb01 v4) */}
              <div className='nb-booking-card-experience-footer'>
                {experienceAudience && (
                  <div className='nb-booking-card-audience'>{experienceAudience}</div>
                )}
                <div
                  role='button'
                  tabIndex={0}
                  className='nb-booking-card-view-details'
                  onClick={handleViewExperience}
                  onKeyDown={handleKeyActivate(handleViewExperience)}
                  aria-label={`View details of ${item.title}`}
                >
                  VIEW DETAILS
                </div>
              </div>
            </div>
          </>
        ) : (
          /* INFO BOX clasico — Introduction / Expertise */
          <div className='nb-booking-card-info-box'>
            <div className='nb-booking-card-info-row'>
              <div className='nb-booking-card-info-label'>TOPIC</div>
              <div className='nb-booking-card-info-value'>{item.topic || 'N/A'}</div>
            </div>
            <div className='nb-booking-card-info-divider'></div>
            <div className='nb-booking-card-info-row'>
              <div className='nb-booking-card-info-label'>MESSAGE</div>
              <div className='nb-booking-card-info-content'>
                <div className='nb-booking-card-info-value text-truncate-introduction'>
                  {item.message || 'N/A'}
                </div>
                {shouldOfferReadMore && (
                  <div
                    role='button'
                    tabIndex={0}
                    className='nb-booking-card-read-more'
                    onClick={handleOpenDetail}
                    onKeyDown={handleKeyActivate(handleOpenDetail)}
                    aria-label={`Read more about ${item.title}`}
                  >
                    READ MORE
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* BOTTOM ACTIONS
            Regla PR-I-84 (Armando 2026-09-09):
            - active/pending (los 3 tipos): SEND MESSAGE + Full Policy / Cancel.
            - past (los 3 tipos): sólo SEND MESSAGE.
            - Experience abre detalle via VIEW DETAILS (dentro del box), no aquí. */}
        <div className='nb-booking-card-actions'>
          {!hideSendMessage &&
            (isSendMessageDisabled ? (
              <div
                className='nb-booking-card-read-more nb-booking-card-read-more--disabled'
                aria-disabled='true'
                title={sendMessageTooltip}
              >
                {sendMessageLabel}
              </div>
            ) : expertiseConversationUuid ? (
              // Expertise ya trae `conversationUuid` en el item -> saltamos directo a
              // /messages con el mismo query param que la card del lado experto en
              // /my-expertise (deep-link por uuid), sin pasar por get-or-create.
              <Link
                to={`/messages?uuid=${expertiseConversationUuid}`}
                className='nb-booking-card-read-more'
                aria-label={`Send message about ${item.title}`}
              >
                {sendMessageLabel}
              </Link>
            ) : (
              <div
                role='button'
                tabIndex={0}
                className='nb-booking-card-read-more'
                onClick={handleSendMessage}
                onKeyDown={handleKeyActivate(handleSendMessage)}
                aria-label={`Send message about ${item.title}`}
              >
                {sendMessageLabel}
              </div>
            ))}
          {showPolicyBlock && (
            <div className='nb-booking-card-policy' aria-label='Booking policy actions'>
              {/* PR-I-84 (Armando 2026-09-09): mientras no tengamos los links
                  reales, oculto el prefijo "Non-Refundable. Read". Full Policy
                  y Cancel siguen visibles como no-op; sus modales llegan en PR-I-85. */}
              <span
                role='button'
                tabIndex={0}
                className='nb-booking-card-policy-link'
                aria-label='Read full booking policy'
                onClick={handleOpenDetail}
                onKeyDown={handleKeyActivate(handleOpenDetail)}
              >
                Full Policy
              </span>
              <span className='nb-booking-card-policy-text'> - </span>
              <span
                role='button'
                tabIndex={0}
                className='nb-booking-card-policy-link'
                aria-label={`Cancel ${TYPE_LABEL[item.type].toLowerCase()} booking`}
                onClick={handleOpenDetail}
                onKeyDown={handleKeyActivate(handleOpenDetail)}
              >
                Cancel
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BookingCard
